/**
 * Rider Form Component
 * 
 * Reusable form for creating and editing riders with:
 * - Form validation
 * - Pre-population for edit mode
 * - Error handling
 * - Loading states
 * 
 * Requirements: 4.5, 4.6, 4.7
 */

import { useState, useEffect } from 'react';
import { Rider, CreateRiderDto, UpdateRiderDto, Group, Event, restClient } from '../../../api/rest-client';
import { Button } from '../../common/Button';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { filterRaceEvents, sortRaceEventsByStartTime, getRegistrationStatus, buildRegistrationMap } from '../../../utils/raceRegistration';
import { composeName, splitName, isValidName } from '../../../utils/name';

export interface RiderFormProps {
  rider?: Rider;
  groups: Group[];
  onSubmit: (data: CreateRiderDto | UpdateRiderDto) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  restrictedFields?: boolean; // If true, disable Category, Group, Status, and Type fields
  restrictSeasons?: boolean; // If true, disable Seasons field (admin-only)
  allowedFields?: string[]; // If provided, only these fields are editable (for associated users)
  hideRaceRegistration?: boolean; // If true, hide race registration section (for non-associated "user" role)
}

interface FormData {
  firstName: string;
  lastName: string;
  category: string;
  group: string;
  grade: string;
  level: string;
  type: '' | 'Student' | 'Coach';
  status: 'active' | 'inactive' | 'graduated';
  seasons: string[]; // Changed from single season to array of seasons
  birthday: string; // ISO 8601 format: YYYY-MM-DD
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  type?: string;
  category?: string;
  group?: string;
  level?: string;
  seasons?: string;
  birthday?: string;
  general?: string;
}

/**
 * Validate form data
 * Requirements: 4.5
 */
export function validateForm(data: FormData, options: { isCreate: boolean; hadBirthday: boolean }): FormErrors {
  const errors: FormErrors = {};

  if (!data.type) {
    errors.type = 'Type is required';
  }

  if (!data.firstName.trim()) {
    errors.firstName = 'First name is required';
  } else if (!isValidName(data.firstName)) {
    errors.firstName = 'First name is invalid (max 100 characters, no < or > characters)';
  }

  if (!data.lastName.trim()) {
    errors.lastName = 'Last name is required';
  } else if (!isValidName(data.lastName)) {
    errors.lastName = 'Last name is invalid (max 100 characters, no < or > characters)';
  }

  if (!data.group) {
    errors.group = 'Group is required';
  }

  if (data.seasons.length === 0) {
    errors.seasons = 'At least one season is required';
  }

  if (data.type === 'Coach' && !data.level) {
    errors.level = 'Level is required for coaches';
  }

  // Birthday: required on create; on edit, required only if the rider originally
  // had a birthday on file (legacy riders without one may still be saved — BR-6).
  const birthdayTrimmed = (data.birthday || '').trim();
  const birthdayRequired = options.isCreate || options.hadBirthday;
  if (!birthdayTrimmed) {
    if (birthdayRequired) {
      errors.birthday = 'Birthday is required';
    }
  } else {
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!dateRegex.test(birthdayTrimmed)) {
      errors.birthday = 'Invalid date format. Use MM/DD/YYYY';
    } else {
      // Validate it's a real date
      const [month, day, year] = birthdayTrimmed.split('/').map(Number);
      const date = new Date(year, month - 1, day);
      if (date.getMonth() !== month - 1 || date.getDate() !== day || date.getFullYear() !== year) {
        errors.birthday = 'Invalid date';
      }
    }
  }

  return errors;
}

/**
 * Get current season (current year)
 */
function getCurrentSeason(): string {
  return new Date().getFullYear().toString();
}

/**
 * Rider Form Component
 */
export function RiderForm({
  rider,
  groups,
  onSubmit,
  onCancel,
  isLoading = false,
  restrictedFields = false,
  restrictSeasons = false,
  allowedFields,
  hideRaceRegistration = false,
}: RiderFormProps) {
  const isEditMode = !!rider;

  // Helper function to check if a field is editable
  const isFieldEditable = (fieldName: string): boolean => {
    // If allowedFields is specified, only those fields are editable
    if (allowedFields) {
      return allowedFields.includes(fieldName);
    }
    // Otherwise, use the restrictedFields logic
    return true;
  };

  // Form state
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    category: '',
    group: '',
    grade: '',
    level: '',
    type: isEditMode ? 'Student' : '',
    status: 'active',
    seasons: [getCurrentSeason()], // Default to current season
    birthday: '', // Empty by default; required on create
  });

  // Tracks whether the rider being edited originally had a birthday on file.
  // Used to allow saving legacy riders that have none (BR-6) while still
  // requiring birthday on create and for riders that already have one.
  const [hadBirthday, setHadBirthday] = useState(false);

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([getCurrentSeason()]); // Fetch from API

  // Race registration state
  const [raceEvents, setRaceEvents] = useState<Event[]>([]);
  const [raceEventsLoading, setRaceEventsLoading] = useState(false);
  const [raceEventsError, setRaceEventsError] = useState<string | null>(null);
  const [checkedEventIds, setCheckedEventIds] = useState<Set<string>>(new Set());
  const [fullRaceSeries, setFullRaceSeries] = useState(false);

  // Fetch available seasons from API (only for edit mode — create only shows current season)
  useEffect(() => {
    if (!isEditMode) return; // Create mode: only current season

    const fetchSeasons = async () => {
      try {
        const config = await restClient.getSeasonConfig();
        setAvailableSeasons(config.availableSeasons);
      } catch (err) {
        console.error('Failed to fetch season config:', err);
      }
    };

    fetchSeasons();
  }, [isEditMode]);

  // Pre-populate form for edit mode
  // Requirements: 4.6
  useEffect(() => {
    if (rider) {
      // Handle type: 'Rider' maps to 'Student' in the form (for legacy compatibility)
      const formType = rider.type === 'Rider' ? 'Student' : (rider.type || 'Student');
      
      // Use seasons_active from the rider record
      const initialSeasons = rider.seasons_active && rider.seasons_active.length > 0
        ? rider.seasons_active
        : (rider.season ? [rider.season] : [getCurrentSeason()]);
      
      // Convert ISO date (YYYY-MM-DD) to MM/DD/YYYY for display
      let formattedBirthday = '';
      if (rider.birthday) {
        const [year, month, day] = rider.birthday.split('-');
        formattedBirthday = `${month}/${day}/${year}`;
      }
      setHadBirthday(!!(rider.birthday && rider.birthday.trim()));

      // Derive first/last name: prefer explicit fields, else best-effort split
      // of the legacy single `name` (admin can correct before saving — BR-7).
      let firstName = rider.first_name || '';
      let lastName = rider.last_name || '';
      if (!firstName && !lastName && rider.name) {
        const [splitFirst, splitLast] = splitName(rider.name);
        firstName = splitFirst;
        lastName = splitLast;
      }

      setFormData({
        firstName,
        lastName,
        category: rider.category || '',
        group: rider.group || '',
        grade: rider.grade || '',
        level: rider.level || '',
        type: formType as '' | 'Student' | 'Coach',
        status: rider.status || 'active',
        seasons: initialSeasons,
        birthday: formattedBirthday,
      });
    }
  }, [rider]);

  // Fetch race events for current season and pre-populate checkboxes
  // Requirements: 2.1, 2.4, 2.5
  useEffect(() => {
    if (hideRaceRegistration || formData.type === 'Coach') return;

    const fetchRaceEvents = async () => {
      setRaceEventsLoading(true);
      setRaceEventsError(null);
      try {
        const events = await restClient.getEvents(getCurrentSeason());
        const filtered = filterRaceEvents(events);
        const sorted = sortRaceEventsByStartTime(filtered);
        setRaceEvents(sorted);

        // Pre-populate from rider.race_registrations
        if (rider?.race_registrations) {
          const preChecked = new Set<string>();
          for (const event of sorted) {
            if (getRegistrationStatus(rider.race_registrations, event.event_id)) {
              preChecked.add(event.event_id);
            }
          }
          setCheckedEventIds(preChecked);
          // Auto-detect full series if all race events are checked
          if (sorted.length > 0 && preChecked.size === sorted.length) {
            setFullRaceSeries(true);
          }
        }
      } catch (err) {
        console.error('Failed to fetch race events:', err);
        setRaceEventsError('Unable to load race events');
      } finally {
        setRaceEventsLoading(false);
      }
    };

    fetchRaceEvents();
  }, [formData.type, hideRaceRegistration, rider?.race_registrations]);

  /**
   * Handle input change
   */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // Clear type-specific fields when switching type
      if (name === 'type') {
        if (value === 'Coach') {
          updated.category = '';
          updated.grade = '';
        } else if (value === 'Student') {
          updated.level = '';
        }
      }
      return updated;
    });
    
    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  /**
   * Handle season checkbox change
   */
  const handleSeasonChange = (season: string, checked: boolean) => {
    setFormData(prev => {
      const newSeasons = checked
        ? [...prev.seasons, season]
        : prev.seasons.filter(s => s !== season);
      
      return { ...prev, seasons: newSeasons };
    });
    
    // Clear error for seasons field
    if (errors.seasons) {
      setErrors(prev => ({ ...prev, seasons: undefined }));
    }
  };

  /**
   * Handle race registration checkbox change
   * Requirements: 2.6
   */
  const handleRaceRegistrationChange = (eventId: string, checked: boolean) => {
    setCheckedEventIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(eventId);
      } else {
        next.delete(eventId);
      }
      return next;
    });
  };

  /**
   * Handle Full Race Series toggle — checks all events and grays out individual boxes
   */
  const handleFullRaceSeriesChange = (checked: boolean) => {
    setFullRaceSeries(checked);
    if (checked) {
      setCheckedEventIds(new Set(raceEvents.map(e => e.event_id)));
    } else {
      setCheckedEventIds(new Set());
    }
  };

  /**
   * Handle blur - mark field as touched and validate
   */
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Run validation on blur
    const validationErrors = validateForm(formData, { isCreate: !isEditMode, hadBirthday });
    setErrors(validationErrors);
  };

  /**
   * Handle form submission
   * Requirements: 4.5, 4.6
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all required fields as touched
    setTouched({
      type: true,
      firstName: true,
      lastName: true,
      category: true,
      group: true,
      level: true,
      seasons: true,
      birthday: true,
    });

    // Validate form
    const validationErrors = validateForm(formData, { isCreate: !isEditMode, hadBirthday });
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setErrors({});

    try {
      // Submit with all selected seasons
      const submitData: any = {
        rider_id: isEditMode && rider ? rider.rider_id : crypto.randomUUID(),
        name: composeName(formData.firstName, formData.lastName),
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        category: (formData.category || '').trim() || undefined,
        group: formData.group,
        grade: (formData.grade || '').trim() || undefined,
        level: formData.type === 'Coach' ? (formData.level || '').trim() || undefined : undefined,
        type: formData.type,
        status: formData.status,
        season: formData.seasons[0], // Primary season for the current record
        seasons_active: formData.seasons, // All selected seasons for backend
        birthday: undefined, // Will be set below if valid
      };

      // Convert MM/DD/YYYY to ISO format (YYYY-MM-DD) for backend
      if ((formData.birthday || '').trim()) {
        const [month, day, year] = (formData.birthday || '').trim().split('/');
        submitData.birthday = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      if (isEditMode && rider) {
        submitData.rider_id = rider.rider_id;
        submitData.disabled = "0";
      }

      // Include race_registrations map for Student/Rider types
      // Requirements: 2.6
      const isStudentOrRider = formData.type === 'Student';
      if (isStudentOrRider && !hideRaceRegistration && raceEvents.length > 0) {
        submitData.race_registrations = buildRegistrationMap(raceEvents, checkedEventIds);
      }

      await onSubmit(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({
        general: error instanceof Error ? error.message : 'Failed to save rider',
      });
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* General Error */}
      {errors.general && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800 text-sm">{errors.general}</p>
        </div>
      )}

      {/* Type Selection */}
      <div>
        <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
          Type <span className="text-red-500">*</span>
        </label>
        <select
          id="type"
          name="type"
          data-testid="rider-form-type-select"
          value={formData.type}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={submitting || isLoading || isEditMode || restrictedFields || !isFieldEditable('type')}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            touched.type && errors.type ? 'border-red-500' : 'border-gray-300'
          } ${isEditMode || restrictedFields || !isFieldEditable('type') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        >
          <option value="">-- Select Type --</option>
          <option value="Student">Student</option>
          <option value="Coach">Coach</option>
        </select>
        {touched.type && errors.type && (
          <p className="mt-1 text-sm text-red-600">{errors.type}</p>
        )}
        {isEditMode ? (
          <p className="mt-1 text-xs text-gray-500">Type cannot be changed after a rider is created</p>
        ) : (restrictedFields || !isFieldEditable('type')) && (
          <p className="mt-1 text-xs text-gray-500">Contact an admin to change this field</p>
        )}
      </div>

      {/* First name */}
      <div>
        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
          First name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          data-testid="rider-form-first-name"
          value={formData.firstName}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={submitting || isLoading || !isFieldEditable('first_name')}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            touched.firstName && errors.firstName
              ? 'border-red-500'
              : 'border-gray-300'
          } ${!isFieldEditable('first_name') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="e.g., John"
        />
        {touched.firstName && errors.firstName && (
          <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
        )}
        {!isFieldEditable('first_name') && (
          <p className="mt-1 text-xs text-gray-500">Contact an admin to change this field</p>
        )}
      </div>

      {/* Last name */}
      <div>
        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
          Last name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          data-testid="rider-form-last-name"
          value={formData.lastName}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={submitting || isLoading || !isFieldEditable('last_name')}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            touched.lastName && errors.lastName
              ? 'border-red-500'
              : 'border-gray-300'
          } ${!isFieldEditable('last_name') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="e.g., Doe"
        />
        {touched.lastName && errors.lastName && (
          <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
        )}
        {!isFieldEditable('last_name') && (
          <p className="mt-1 text-xs text-gray-500">Contact an admin to change this field</p>
        )}
      </div>

      {/* Birthday */}
      <div>
        <label htmlFor="birthday" className="block text-sm font-medium text-gray-700 mb-1">
          Birthday <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="birthday"
          name="birthday"
          value={formData.birthday}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={submitting || isLoading || !isFieldEditable('birthday')}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            touched.birthday && errors.birthday
              ? 'border-red-500'
              : 'border-gray-300'
          } ${!isFieldEditable('birthday') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="MM/DD/YYYY"
        />
        {touched.birthday && errors.birthday && (
          <p className="mt-1 text-sm text-red-600">{errors.birthday}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">Format: MM/DD/YYYY (e.g., 01/15/2010)</p>
        {!isFieldEditable('birthday') && (
          <p className="mt-1 text-xs text-gray-500">Contact an admin to change this field</p>
        )}
      </div>

      {/* Category (Students only) */}
      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
          Category (Optional)
        </label>
        <select
          id="category"
          name="category"
          value={formData.type === 'Coach' ? '' : formData.category}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={submitting || isLoading || restrictedFields || !isFieldEditable('category') || formData.type === 'Coach'}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            touched.category && errors.category
              ? 'border-red-500'
              : 'border-gray-300'
          } ${restrictedFields || !isFieldEditable('category') || formData.type === 'Coach' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        >
          <option value="">-- Select Category --</option>
          <option value="Varsity">Varsity</option>
          <option value="JV1">JV1</option>
          <option value="JV2">JV2</option>
          <option value="Freshman">Freshman</option>
          <option value="8th Grade">8th Grade</option>
          <option value="7th Grade">7th Grade</option>
          <option value="6th Grade">6th Grade</option>
        </select>
        {touched.category && errors.category && (
          <p className="mt-1 text-sm text-red-600">{errors.category}</p>
        )}
        {formData.type === 'Coach' && (
          <p className="mt-1 text-xs text-gray-500">Category is not applicable for coaches</p>
        )}
        {(restrictedFields || !isFieldEditable('category')) && formData.type !== 'Coach' && (
          <p className="mt-1 text-xs text-gray-500">Contact an admin to change this field</p>
        )}
      </div>

      {/* Group */}
      <div>
        <label htmlFor="group" className="block text-sm font-medium text-gray-700 mb-1">
          Group <span className="text-red-500">*</span>
        </label>
        <select
          id="group"
          name="group"
          value={formData.group}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={submitting || isLoading || restrictedFields || !isFieldEditable('group')}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            touched.group && errors.group
              ? 'border-red-500'
              : 'border-gray-300'
          } ${restrictedFields || !isFieldEditable('group') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        >
          <option value="">Select group</option>
          {[...groups].sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map(group => (
            <option key={group.group_id} value={group.name}>
              {group.name}
            </option>
          ))}
        </select>
        {touched.group && errors.group && (
          <p className="mt-1 text-sm text-red-600">{errors.group}</p>
        )}
        {(restrictedFields || !isFieldEditable('group')) && (
          <p className="mt-1 text-xs text-gray-500">Contact an admin to change this field</p>
        )}
      </div>

      {/* Grade (for Students — disabled for Coaches) */}
      <div>
        <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-1">
          Grade (Optional)
        </label>
        <select
          id="grade"
          name="grade"
          value={formData.type === 'Coach' ? '' : formData.grade}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={submitting || isLoading || !isFieldEditable('grade') || formData.type === 'Coach'}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            !isFieldEditable('grade') || formData.type === 'Coach' ? 'bg-gray-100 cursor-not-allowed border-gray-300' : 'border-gray-300'
          }`}
        >
          <option value="">-- Select Grade --</option>
          <option value="12th">12th</option>
          <option value="11th">11th</option>
          <option value="10th">10th</option>
          <option value="9th">9th</option>
          <option value="8th">8th</option>
          <option value="7th">7th</option>
          <option value="6th">6th</option>
        </select>
        {formData.type === 'Coach' && (
          <p className="mt-1 text-xs text-gray-500">Grade is not applicable for coaches</p>
        )}
        {!isFieldEditable('grade') && formData.type !== 'Coach' && (
          <p className="mt-1 text-xs text-gray-500">Contact an admin to change this field</p>
        )}
      </div>

      {/* Level (for Coaches — disabled for Students) */}
      <div>
        <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-1">
          Level {formData.type === 'Coach' && <span className="text-red-500">*</span>}
        </label>
        <select
          id="level"
          name="level"
          value={formData.type === 'Student' || formData.type === '' ? '' : formData.level}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={submitting || isLoading || !isFieldEditable('level') || formData.type !== 'Coach'}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            touched.level && errors.level ? 'border-red-500' :
            !isFieldEditable('level') || formData.type !== 'Coach' ? 'bg-gray-100 cursor-not-allowed border-gray-300' : 'border-gray-300'
          }`}
        >
          <option value="">-- Select Level --</option>
          <option value="None">None</option>
          <option value="1">Level 1</option>
          <option value="2">Level 2</option>
          <option value="3">Level 3</option>
        </select>
        {touched.level && errors.level && (
          <p className="mt-1 text-sm text-red-600">{errors.level}</p>
        )}
        {formData.type !== 'Coach' && formData.type !== '' && (
          <p className="mt-1 text-xs text-gray-500">Level is not applicable for students</p>
        )}
        {!isFieldEditable('level') && formData.type === 'Coach' && (
          <p className="mt-1 text-xs text-gray-500">Contact an admin to change this field</p>
        )}
      </div>

      {/* Status */}
      <div>
        <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
          Status <span className="text-red-500">*</span>
        </label>
        <select
          id="status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          disabled={submitting || isLoading || restrictedFields || !isFieldEditable('status')}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            restrictedFields || !isFieldEditable('status') ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
          }`}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="graduated">Graduated</option>
        </select>
        {(restrictedFields || !isFieldEditable('status')) && (
          <p className="mt-1 text-xs text-gray-500">Contact an admin to change this field</p>
        )}
      </div>

      {/* Seasons */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Seasons <span className="text-red-500">*</span>
        </label>
        <div className={`space-y-2 p-4 rounded-md border ${
          restrictSeasons || !isFieldEditable('seasons') ? 'bg-gray-100 border-gray-300' : 'bg-gray-50 border-gray-200'
        }`}>
          {availableSeasons.map(season => {
            const isPastSeason = isEditMode && parseInt(season) < new Date().getFullYear();
            const isDisabled = submitting || isLoading || restrictSeasons || !isFieldEditable('seasons') || isPastSeason;
            return (
              <div key={season} className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id={`season-${season}`}
                  checked={formData.seasons.includes(season)}
                  onChange={(e) => handleSeasonChange(season, e.target.checked)}
                  disabled={isDisabled}
                  className={`w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-2 focus:ring-primary-500 ${
                    isDisabled ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                />
                <label htmlFor={`season-${season}`} className={`text-sm font-medium ${
                  isPastSeason ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'
                } ${isDisabled && !isPastSeason ? 'cursor-not-allowed' : !isDisabled ? 'cursor-pointer' : ''}`}>
                  {season}
                </label>
              </div>
            );
          })}
        </div>
        {touched.seasons && errors.seasons && (
          <p className="mt-1 text-sm text-red-600">{errors.seasons}</p>
        )}
        {(restrictSeasons || !isFieldEditable('seasons')) && (
          <p className="mt-1 text-xs text-gray-500">Contact an admin to change this field</p>
        )}
      </div>

      {/* Race Registration (Students/Riders only) */}
      {/* Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.7, 2.8 */}
      {formData.type !== 'Coach' && formData.type !== '' && !hideRaceRegistration && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Race Registration</h3>
          {raceEventsLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <LoadingSpinner size="sm" />
              <span>Loading race events...</span>
            </div>
          )}
          {raceEventsError && (
            <p className="text-sm text-red-600">{raceEventsError}</p>
          )}
          {!raceEventsLoading && !raceEventsError && raceEvents.length === 0 && (
            <p className="text-sm text-gray-500">No race events found for this season</p>
          )}
          {!raceEventsLoading && !raceEventsError && raceEvents.length > 0 && (
            <div className="space-y-2 p-4 rounded-md border bg-gray-50 border-gray-200">
              {/* Full Race Series toggle */}
              <div className="flex items-center gap-3 pb-2 mb-2 border-b border-gray-300">
                <input
                  type="checkbox"
                  id="full-race-series"
                  checked={fullRaceSeries}
                  onChange={(e) => handleFullRaceSeriesChange(e.target.checked)}
                  disabled={submitting || isLoading}
                  className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                />
                <label
                  htmlFor="full-race-series"
                  className="text-sm font-semibold text-gray-900 cursor-pointer"
                >
                  Full Race Series
                </label>
              </div>
              {raceEvents.map(event => (
                <div key={event.event_id} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id={`race-reg-${event.event_id}`}
                    checked={checkedEventIds.has(event.event_id)}
                    onChange={(e) => handleRaceRegistrationChange(event.event_id, e.target.checked)}
                    disabled={submitting || isLoading || fullRaceSeries}
                    className={`w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-2 focus:ring-primary-500 ${
                      fullRaceSeries ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  />
                  <label
                    htmlFor={`race-reg-${event.event_id}`}
                    className={`text-sm font-medium cursor-pointer ${
                      fullRaceSeries ? 'text-gray-400' : 'text-gray-700'
                    }`}
                  >
                    {event.event_name}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Form Actions */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={submitting || isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={submitting || isLoading}
        >
          {submitting || isLoading ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              {isEditMode ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            <>{isEditMode ? 'Update Rider' : 'Create Rider'}</>
          )}
        </Button>
      </div>
    </form>
  );
}

export default RiderForm;
